<script setup lang="ts">
    import { computed, inject, onMounted, reactive } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { FeatureService } from '@shared/directives/Feature.service';
    import { ContextAccess, loadCurrentSubscription } from '@shared/Root/Context.access';
    import Loading from '@shared/components/Loading.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import { formatNumericText } from '@shared/utils/formatNumericText';
    import { formatDate } from '@shared/utils/formatDateTime';
    import { getUsage, getAITokenConsumption, AccountUsage } from './AccountSubscription.access';
    import { Chart as ChartJS, BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend, ChartOptions, ChartData, TooltipItem } from 'chart.js';
    import { Chart } from 'vue-chartjs';
    import { applyChartDefaults } from '@shared/modules/chartjs-defaults';

    ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, BarController);
    applyChartDefaults();

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);

    const state = reactive<{
        isLoading: boolean;
        loadError: boolean;
        usage: AccountUsage | null;
        aiTokenConsumption: number | null;
        chartData: ChartData<'bar'> | null;
        chartOptions: ChartOptions<'bar'> | null;
    }>({
        isLoading: true,
        loadError: false,
        usage: null,
        aiTokenConsumption: null,
        chartData: null,
        chartOptions: null,
    });

    const subscription = computed(() => contextAccess.state.currentSubscription);

    const isOverEmailLimit = computed(() => !!state.usage && !!subscription.value && state.usage.EmailCount > subscription.value.EmailLimit);
    const emailUsagePercent = computed(() => {
        if (!state.usage || !subscription.value) return 0;
        const limit = subscription.value.EmailLimit;
        if (isOverEmailLimit.value || limit <= 0) return 100;
        return (state.usage.EmailCount / limit) * 100;
    });

    const isOverContactLimit = computed(() => !!state.usage && !!subscription.value && state.usage.ContactCount > subscription.value.ContactLimit);
    const contactUsagePercent = computed(() => {
        if (!state.usage || !subscription.value) return 0;
        const limit = subscription.value.ContactLimit;
        if (isOverContactLimit.value || limit <= 0) return 100;
        return (state.usage.ContactCount / limit) * 100;
    });

    onMounted(async () => {
        try {
            if (!contextAccess.state.currentSubscription) {
                contextAccess.state.currentSubscription = await loadCurrentSubscription();
            }

            state.usage = await getUsage();

            if (featureAccess.hasFeature('AIAssistant.Assistant')) {
                state.aiTokenConsumption = await getAITokenConsumption();
            }

            setupChart();
        } catch {
            state.loadError = true;
        } finally {
            state.isLoading = false;
        }
    });

    function setupChart(): void {
        if (!state.usage) return;

        const dailyUsage = state.usage.DailyUsage;

        state.chartData = {
            labels: Object.keys(dailyUsage).map((item) => new Date(item).getDate()),
            datasets: [
                {
                    label: $Resources.AccountChartIssueCount,
                    data: Object.values(dailyUsage),
                    backgroundColor: 'rgba(31,169,176,1)',
                    hoverBackgroundColor: 'rgba(31,169,176,.9)',
                },
            ],
        };

        state.chartOptions = {
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    intersect: false,
                    filter: (tooltipItem: TooltipItem<'bar'>, index, array, data) => {
                        const value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.dataIndex];
                        return value !== 0;
                    },
                    callbacks: {
                        title: (tooltipItems: TooltipItem<'bar'>[]) => {
                            if (tooltipItems.length === 0) return '';
                            const label = Object.keys(dailyUsage)[tooltipItems[0].dataIndex];
                            return label ? new Date(label).toLocaleDateString() : '';
                        },
                    },
                },
            },
            scales: {
                y: {
                    ticks: {
                        display: false,
                    },
                },
                x: {
                    ticks: {
                        font: { size: 11 },
                    },
                    grid: {
                        display: false,
                    },
                },
            },
        };
    }

    function cancellationDeadline(paidUntil: string): string {
        const d = new Date(paidUntil);
        d.setMonth(d.getMonth() - 3);
        return formatDate(d);
    }
</script>

<template>
    <div class="max-900">
        <h1 class="h2 m-lg-b">
            {{ $Resources.ServiceSubscriptionTitle }}
            <small v-if="subscription">({{ subscription.ClientCompanyName }})</small>
        </h1>

        <div v-if="state.isLoading">
            <Loading show />
            <p class="text-center">{{ $Resources.ServiceSubscriptionCalculateSize }}</p>
        </div>

        <BsAlert v-if="state.loadError" :type="AlertTypes.Danger" :dismissible="false" :title="$Resources.ErrorHeading" :content="$Resources.ErrorContactSupport" />

        <div v-if="!state.isLoading && !state.loadError && subscription && state.usage">
            <h2 class="h3">
                {{ subscription.ServiceLevelPrice.ServiceSubscriptionTier.Name }}
                {{ formatNumericText(subscription.ServiceLevelPrice.ServiceLevel.Name, { numberWithSpace: true }) }}
            </h2>

            <div class="stat-highlights panel panel-default">
                <div class="stat-highlights-body ug-bars">
                    <div class="row">
                        <div class="col-md-6">
                            <p class="text-md m-xs-b">
                                {{ $Resources.ClientCompanyEmailCountLabel }}
                                <span v-if="subscription.EmailLimitMonths == 1">{{ $Resources.ClientCompanyEmailCountMonthLabel }}</span>
                                <span v-else>
                                    <span>{{ subscription.EmailLimitMonths }}</span>
                                    {{ $Resources.ClientCompanyEmailCountMonthsLabel }}
                                </span>
                            </p>

                            <p class="value">
                                <span>{{ formatNumericText(state.usage.EmailCount, { numberWithSpace: true }) }}</span>
                                <small>
                                    /
                                    <span>{{ formatNumericText(subscription.EmailLimit, { numberWithSpace: true }) }}</span>
                                </small>
                            </p>

                            <div
                                class="bar"
                                role="progressbar"
                                :aria-valuenow="Math.min(state.usage.EmailCount, subscription.EmailLimit)"
                                aria-valuemin="0"
                                :aria-valuemax="subscription.EmailLimit"
                                :aria-label="$Resources.ClientCompanyEmailCountLabel">
                                <span
                                    :style="{ width: emailUsagePercent + '%' }"
                                    :class="['bar-value', 'pull-left', isOverEmailLimit ? 'bg-unsubscribed' : 'received']"></span>
                            </div>

                            <p v-if="!isOverEmailLimit">
                                <strong>{{ formatNumericText(subscription.EmailLimit - state.usage.EmailCount, { numberWithSpace: true }) }}</strong>
                                {{ $Resources.ClientCompanySubscriptionMailingsUntilUpgradeInfo }}
                            </p>
                        </div>
                        <div class="col-md-6">
                            <p class="text-md m-xs-b">
                                {{ $Resources.ClientCompanyContactCountLabel }}
                                <span v-if="subscription.ContactLimitMonths == 1">{{ $Resources.ClientCompanyEmailCountMonthLabel }}</span>
                                <span v-else>
                                    <span>{{ subscription.ContactLimitMonths }}</span>
                                    {{ $Resources.ClientCompanyEmailCountMonthsLabel }}
                                </span>
                            </p>

                            <p class="value">
                                <span>{{ formatNumericText(state.usage.ContactCount, { numberWithSpace: true }) }}</span>
                                <small>
                                    /
                                    <span>{{ formatNumericText(subscription.ContactLimit, { numberWithSpace: true }) }}</span>
                                </small>
                            </p>

                            <div
                                class="bar"
                                role="progressbar"
                                :aria-valuenow="Math.min(state.usage.ContactCount, subscription.ContactLimit)"
                                aria-valuemin="0"
                                :aria-valuemax="subscription.ContactLimit"
                                :aria-label="$Resources.ClientCompanyContactCountLabel">
                                <span
                                    :style="{ width: contactUsagePercent + '%' }"
                                    :class="['bar-value', 'pull-left', isOverContactLimit ? 'bg-unsubscribed' : 'received']"></span>
                            </div>

                            <p v-if="!isOverContactLimit">
                                <strong>{{ formatNumericText(subscription.ContactLimit - state.usage.ContactCount, { numberWithSpace: true }) }}</strong>
                                {{ $Resources.ClientCompanySubscriptionContactsUntilUpgradeInfo }}
                            </p>
                        </div>
                    </div>

                    <h3 class="h4">{{ $Resources.AccountChartIssueCountHeading }}</h3>

                    <div v-if="state.usage.DailyUsage" class="chart-container">
                        <Chart v-if="state.chartData && state.chartOptions" type="bar" :data="state.chartData" :options="state.chartOptions" />
                    </div>
                </div>
            </div>

            <BsAlert
                v-if="!subscription.ServiceLevelPrice.ServiceSubscriptionTier.IsFree"
                :type="AlertTypes.Info"
                :dismissible="false"
                :content="$Resources.ClientCompanySubscriptionCalculateCostInfo" />

            <div class="row">
                <div class="col-md-4">
                    <p class="m-lg-b">
                        <span class="label-look d-block">{{ $Resources.ContractStartLabel }}:</span>
                        <span class="align-with-input">{{ formatDate(subscription.Start) }}</span>
                    </p>
                </div>
                <div class="col-md-4">
                    <p class="m-lg-b">
                        <span class="label-look d-block">{{ $Resources.NextRenewalDateLabel }}:</span>
                        <span class="align-with-input" v-if="subscription.PaidUntil">{{ formatDate(subscription.PaidUntil) }}</span>
                        <span class="align-with-input" v-else>{{ $Resources.ValueIsMissing }}</span>
                    </p>
                </div>
                <div class="col-md-4" v-if="subscription.BillingEnd">
                    <p class="m-lg-b">
                        <span class="label-look d-block">{{ $Resources.BillingEndLabel }}:</span>
                        <span class="align-with-input c-danger">{{ formatDate(subscription.BillingEnd) }}</span>
                    </p>
                </div>
            </div>

            <BsAlert v-if="subscription.PaidUntil" :type="AlertTypes.Info" :dismissible="false">
                <template #content>
                    {{ $Resources.ContractCancellationDeadlineInfoPart1 }}
                    <strong>{{ cancellationDeadline(subscription.PaidUntil) }}</strong>
                    {{ $Resources.ContractCancellationDeadlineInfoPart2 }}
                </template>
            </BsAlert>
        </div>

        <p class="m-lg-b" v-if="featureAccess.hasFeature('AIAssistant.Assistant') && state.aiTokenConsumption != null">
            {{ formatNumericText(state.aiTokenConsumption, { precision: 2 }) }} % {{ $Resources.AIAssistantConsumptionPostText }}
        </p>
    </div>
</template>

<style scoped>
    .chart-container {
        max-height: 200px;
    }
</style>
